// STRUCTURE-CHANGED §3.2: an inbox item about a reporting-line move (`subject_kind`
// "structure_change") opens this modal: the change in one line, from → to, the agents told, their
// note, and the work addressed to the old leader with "Take over" / "Leave with <old leader>"
// (`POST structure-changes/{id}/reroute`). Read-only when the caller may not re-route.

import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  WorkerBadge,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { LoadError, Loading } from "../screen-parts";
import { FormError } from "../structure/parts";

type View = OperationOutput<"structureChanges.get">;
type Reroute = View["reroute"][number];
type Action = "take_over" | "leave";

const structureChangeKey = (co: string, id: string) =>
  ["company", co, "structure-changes", id] as const;

const KIND_LABEL: Record<Reroute["kind"], string> = {
  task: "Task",
  approval: "Approval",
  status_ask: "Status ask",
};

export function StructureChangeDialog({
  api,
  co,
  id,
  onClose,
}: {
  api: Api;
  co: string;
  id: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const view = useQuery({
    queryKey: structureChangeKey(co, id),
    queryFn: () => api.client.structureChanges.get({ params: { co, id } }),
  });
  const reroute = useMutation({
    mutationFn: (items: { kind: Reroute["kind"]; id: string; action: Action }[]) =>
      api.client.structureChanges.reroute({ params: { co, id }, body: { items } }),
    onSuccess: (next) => qc.setQueryData(structureChangeKey(co, id), next),
  });
  const v = view.data;
  const open = v?.reroute.filter((r) => r.state === "open") ?? [];
  const can = Boolean(v?.can_reroute) && !reroute.isPending;
  const oldLeader = v?.old_leader?.name;
  const act = (items: Reroute[], action: Action) =>
    reroute.mutate(items.map((r) => ({ kind: r.kind, id: r.id, action })));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-slot="structure-change">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Structure change
            <InfoTip label="About structure changes">
              A reporting line moved. Open work addressed to the old leader is listed here: take it
              over or leave it where it is.
            </InfoTip>
          </DialogTitle>
        </DialogHeader>
        {view.isPending ? (
          <Loading label="Loading the change" rows={2} />
        ) : view.isError ? (
          <LoadError what="The change" error={view.error} />
        ) : v ? (
          <div className="flex min-w-0 flex-col gap-4 text-sm">
            <p className="font-medium" data-slot="structure-summary">
              {v.summary}
            </p>
            <p className="flex flex-wrap items-center gap-1.5 text-secondary">
              <span className="font-medium text-foreground">{v.subject_name}</span>
              <span className="line-through decoration-faint">{v.from?.name ?? "the top"}</span>
              <ArrowRightIcon aria-label="now" className="size-3.5 text-faint" />
              <span className="font-medium text-foreground">{v.to?.name ?? "the top"}</span>
            </p>
            {v.agents.length ? (
              <section aria-label="Agents told" className="flex flex-col gap-1.5">
                <h3 className="text-xs text-faint">Agents told</h3>
                <ul className="flex flex-wrap gap-1.5">
                  {v.agents.map((a) => (
                    <li key={a.id}>
                      <Badge variant="neutral" className="gap-1">
                        <WorkerBadge kind={a.kind === "digital_employee" ? "de" : "ag"} size="sm" />
                        {a.name}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {v.note ? (
              <blockquote className="border-l-2 border-border pl-3 text-muted-foreground italic">
                {v.note}
              </blockquote>
            ) : null}
            <section aria-label="To re-route" className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <h3 className="text-xs text-faint">To re-route</h3>
                {v.can_reroute && open.length > 1 ? (
                  <Button
                    size="xs"
                    className="ml-auto"
                    disabled={!can}
                    onClick={() => act(open, "take_over")}
                  >
                    Take over all
                  </Button>
                ) : null}
              </div>
              {v.reroute.length ? (
                <ul className="flex flex-col divide-y divide-subtle">
                  {v.reroute.map((r) => (
                    <li
                      key={`${r.kind}-${r.id}`}
                      data-reroute={r.id}
                      data-state={r.state}
                      className="flex min-w-0 flex-wrap items-center gap-2 py-1.5"
                    >
                      <Badge variant="outline">{KIND_LABEL[r.kind]}</Badge>
                      <span className="min-w-0 flex-1 truncate">{r.title}</span>
                      {r.state !== "open" ? (
                        <Badge variant={r.state === "taken" ? "success" : "neutral"}>
                          {r.state === "taken"
                            ? "Taken over"
                            : `Left with ${oldLeader ?? "old leader"}`}
                        </Badge>
                      ) : v.can_reroute ? (
                        <span className="flex shrink-0 gap-1.5">
                          <Button size="xs" disabled={!can} onClick={() => act([r], "take_over")}>
                            Take over
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            disabled={!can}
                            onClick={() => act([r], "leave")}
                          >
                            {oldLeader ? `Leave with ${oldLeader}` : "Leave"}
                          </Button>
                        </span>
                      ) : (
                        <Badge variant="neutral">Open</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Nothing to re-route.</p>
              )}
            </section>
            <FormError what="Re-routing" error={reroute.error} />
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
